import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { DeviceInfoService } from './deviceInfo.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { SiteModel } from '../models/builder.model';
import { filter } from 'rxjs/operators';
import { SentryService } from './sentry.service';
import { throwError } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class AnalyticsService {
    siteId: string = '';
    locationInfo: any;
    utmObject = {
        referrer: 'direct',
        utmFormat: '',
        utmMedium: '',
        utmCampaign: ''
    };
    subToRoute: boolean = false;
    firstVisit: boolean = true;

    constructor(private http: HttpClient, private sentryService: SentryService, private route: ActivatedRoute, private router: Router, private deviceInfoService: DeviceInfoService) {
    }
    getUtm() {
        try {
            if (this.utmObject.utmCampaign === '' && this.utmObject.utmMedium === '' && this.utmObject.utmFormat === '' && this.utmObject.referrer === 'direct') {
                if (this.route.snapshot.queryParams.utm_source) {
                    this.utmObject = {
                        referrer: this.route.snapshot.queryParams.utm_source,
                        utmFormat: this.route.snapshot.queryParams.utm_source,
                        utmMedium: this.route.snapshot.queryParams.utm_medium,
                        utmCampaign: this.route.snapshot.queryParams.utm_campaign
                    }
                }
            }
        } catch (err) {
            this.sentryService.errorHandling(err, 'analytics-service', err && err.error ? err.error.message : err);
        }
    }
    trackUserNavigation(site: SiteModel) {
        try {
            if (!this.subToRoute) {
                this.subToRoute = false;
                const navEndEvent$ = this.router.events.pipe(
                    filter(e => e instanceof NavigationEnd)
                );
                navEndEvent$.subscribe(async (e: NavigationEnd) => {
                    if (!e.urlAfterRedirects.includes('#')) {
                        await this.eventEmiter(site, e.urlAfterRedirects, 'pageviews', 'trafic');
                        if (this.locationInfo && this.locationInfo['country']) {
                            await this.eventEmiter(site, e.urlAfterRedirects, 'pageviews', 'geo');
                        }
                        await this.eventEmiter(site, e.urlAfterRedirects, 'pageviews', 'device');
                    }
                });
            }
        } catch (err) {
            this.sentryService.errorHandling(err, 'analytics-service', err && err.error ? err.error.message : err);
        }
    }
    trackUserClicks(site: SiteModel) {
        try {
            this.eventEmiter(site, site.siteUrl + this.router.url, 'click', 'trafic');
        } catch (err) {
            this.sentryService.errorHandling(err, 'analytics-service', err && err.error ? err.error.message : err);
        }
    }

    async trackConversion(site: SiteModel, eventType) {
        try {
            this.getLocationInfo().then(async location => {
                this.locationInfo = location;
                await this.eventEmiter(site, site.siteUrl + this.router.url, eventType, 'trafic', this.locationInfo);
                if (this.locationInfo && this.locationInfo['country']) {
                    await this.eventEmiter(site, site.siteUrl + this.router.url, eventType, 'geo', this.locationInfo);
                }
                await this.eventEmiter(site, site.siteUrl + this.router.url, eventType, 'device', this.locationInfo);
            });
        } catch (err) {
            this.sentryService.errorHandling(err, 'analytics-service', err && err.error ? err.error.message : err);
        }
    }
    postAnalytics(siteId: string, url: string, eventType: string, audienceType: string, saleValue: number = 0, saleCurrency: string = 'USD') {
        try {
            return new Promise(async (resolve, reject) => {
                if (!this.locationInfo && audienceType === 'geo') {
                    this.locationInfo = await this.getLocationInfo();
                }
                const deviceInfo = this.deviceInfoService.getDeviceInfo();
                const userAgent = deviceInfo.deviceName ? deviceInfo.device + " / " + deviceInfo.deviceName + " / " + deviceInfo.userAgent + ' ' + deviceInfo.browser_version.toString().split('.')[0] : deviceInfo.device + " / " + deviceInfo.os + " / " + deviceInfo.userAgent + ' ' + deviceInfo.browser_version.toString().split('.')[0];

                if (audienceType !== 'geo') {
                    this.http.post<any>(`${environment.baseUrl}/postAnalytics`, {
                        siteId,
                        createdAt: new Date().getTime() / 1000,
                        url,
                        eventType,
                        referrer: this.utmObject.referrer,
                        ipAddress: this.locationInfo ? this.locationInfo['ip'] : '',
                        userAgent,
                        country: this.locationInfo ? this.locationInfo['country'] : '',
                        audienceType,
                        saleValue,
                        saleCurrency,
                        utmMedium: this.utmObject.utmMedium,
                        utmFormat: this.utmObject.utmFormat,
                        utmCampaign: this.utmObject.utmCampaign,
                        language: deviceInfo.language
                    }, { headers: { Anonymous: 'Anonymous' } }).subscribe(data => {
                        resolve(resolve);
                    }, err => {
                        this.sentryService.errorHandling(err, 'analytics-service', err && err.error ? err.error.message : err);
                        reject(err);
                    });
                } else if (audienceType === 'geo' && this.locationInfo && this.locationInfo['country']) {
                    this.http.post<any>(`${environment.baseUrl}/postAnalytics`, {
                        siteId,
                        createdAt: new Date().getTime() / 1000,
                        url,
                        eventType,
                        referrer: this.utmObject.referrer,
                        ipAddress: this.locationInfo ? this.locationInfo['ip'] : '',
                        userAgent,
                        country: this.locationInfo ? this.locationInfo['country'] : '',
                        audienceType,
                        saleValue,
                        saleCurrency,
                        utmMedium: this.utmObject.utmMedium,
                        utmFormat: this.utmObject.utmFormat,
                        utmCampaign: this.utmObject.utmCampaign,
                        language: deviceInfo.language
                    }, { headers: { Anonymous: 'Anonymous' } }).subscribe(data => {
                        resolve(resolve);
                    }, err => {
                        this.sentryService.errorHandling(err, 'analytics-service', err && err.error ? err.error.message : err);
                        reject(err);
                    });
                }

            });
        } catch (err) {
            this.sentryService.errorHandling(err, 'analytics-service', err && err.error ? err.error.message : err);
        }
    }
    getLocationInfo() {
        try {
            return new Promise((resolve, reject) => {
                this.http.get(`${environment.baseUrl}/getLocation`, { headers: { Anonymous: 'Anonymous' } }).subscribe(data => {
                    resolve({
                        country: data ? data['country'] : '',
                        ip: data ? data['ip'] : ''
                    });
                }, err => {
                    resolve({
                        country: 'No Country Found',
                        ip: '0.0.0.0'
                    });
                    // this.sentryService.errorHandling(err,'analytics-service',err && err.error ?err.error.message:err);
                    // resolve('-');
                });
            });
        } catch (err) {
            this.sentryService.errorHandling(err, 'analytics-service', err && err.error ? err.error.message : err);
        }
    }

    async eventEmiter(site: SiteModel, urlVisited: string, eventType: string, audienceTypes: string, locationInfo = null) {
        try {
            return new Promise(async (resolve, reject) => {
                this.locationInfo = locationInfo;
                await this.postAnalytics(
                    site.siteId,
                    site.siteUrl + urlVisited,
                    eventType,
                    audienceTypes).then(data => {
                        resolve(true);
                    }).catch(err => {
                        resolve(false);
                    });
            });
        } catch (err) {
            this.sentryService.errorHandling(err, 'analytics-service', err && err.error ? err.error.message : err);
        }
    }
    public handleError(error) {
        this.sentryService.errorHandling(error, 'analytics-service', error.error.message ? error.error.message : (error.message ? error.message : error));
        // return throwError(error);
    }
}
